import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Avatar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search,
  Edit,
  Delete,
  Visibility,
  Download,
  Refresh,
  UploadFile,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { adminService, AdminStudent, PaginationInfo } from '../services/adminService';

interface StudentsFilter {
  search: string;
  grade: string;
  isActive: boolean;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

const AdminStudents: React.FC = () => {
  const [students, setStudents] = useState<AdminStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalStudents: 0,
    hasMore: false
  });

  // Filters and search
  const [filters, setFilters] = useState<StudentsFilter>({
    search: '',
    grade: '',
    isActive: true,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Dialog states
  const [selectedStudent, setSelectedStudent] = useState<AdminStudent | null>(null);
  const [showStudentDialog, setShowStudentDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Form data for editing
  const [editFormData, setEditFormData] = useState<Partial<AdminStudent>>({});

  // Bulk import
  const [importOpen, setImportOpen] = useState(false);
  const [importGrade, setImportGrade] = useState('');
  const [importSection, setImportSection] = useState('');
  const [importNames, setImportNames] = useState<string[]>([]);
  const [importFileName, setImportFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResults, setImportResults] = useState<any[] | null>(null);
  const [importSummary, setImportSummary] = useState<{ total: number; created: number; skipped: number } | null>(null);

  // Fetch students
  const fetchStudents = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        limit: 20,
        search: filters.search || undefined,
        grade: filters.grade ? parseInt(filters.grade) : undefined,
        isActive: filters.isActive,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };

      const response = await adminService.getStudents(params);
      
      setStudents(response.data.students);
      setPagination(response.data.pagination);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  // Fetch student details
  const fetchStudentDetails = async (id: string) => {
    try {
      const response = await adminService.getStudent(id);
      setSelectedStudent(response.data.student);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch student details');
    }
  };

  // Update student
  const updateStudent = async () => {
    if (!selectedStudent) return;

    try {
      await adminService.updateStudent(selectedStudent._id, editFormData);
      setShowStudentDialog(false);
      setEditMode(false);
      fetchStudents(pagination.currentPage);
    } catch (err: any) {
      setError(err.message || 'Failed to update student');
    }
  };

  // Delete student
  const deleteStudent = async () => {
    if (!selectedStudent) return;

    try {
      await adminService.deleteStudent(selectedStudent._id);
      setShowDeleteDialog(false);
      setSelectedStudent(null);
      fetchStudents(pagination.currentPage);
    } catch (err: any) {
      setError(err.message || 'Failed to delete student');
    }
  };

  // Handle filter changes
  const handleFilterChange = (field: keyof StudentsFilter, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Handle search
  const handleSearch = () => {
    fetchStudents(1);
  };

  // Handle page change
  const handlePageChange = (event: unknown, newPage: number) => {
    fetchStudents(newPage + 1);
  };

  // Open student dialog
  const openStudentDialog = (student: AdminStudent) => {
    setSelectedStudent(student);
    setEditFormData({
      name: student.name,
      email: student.email,
      grade: student.grade,
      school: student.school,
      schoolRegistrationNumber: student.schoolRegistrationNumber,
      phone: student.phone,
      parentName: student.parentName,
      parentPhone: student.parentPhone,
      isActive: student.isActive,
      role: student.role
    });
    setShowStudentDialog(true);
    fetchStudentDetails(student._id);
  };

  // Export students
  const exportStudents = () => {
    const csvContent = [
      ['Name', 'Email', 'Grade', 'School', 'Phone', 'Parent Name', 'Parent Phone', 'Status', 'Registration Date'],
      ...students.map(student => [
        student.name,
        student.email,
        student.grade.toString(),
        student.school,
        student.phone,
        student.parentName,
        student.parentPhone,
        student.isActive ? 'Active' : 'Inactive',
        new Date(student.registrationDate).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `students_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ---- Bulk import of students ----
  const downloadTemplate = () => {
    const csv = 'name\nRahul Sharma\nPriya Patel\nAarav Mehta\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const openImport = () => {
    setImportOpen(true);
    setImportGrade('');
    setImportSection('');
    setImportNames([]);
    setImportFileName('');
    setImportError(null);
    setImportResults(null);
    setImportSummary(null);
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFileName(file.name);
    setImportError(null);
    setImportResults(null);
    const reader = new FileReader();
    reader.onload = () => {
      const text = (reader.result as string) || '';
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      // drop a header row if it just says "name"
      const rows = lines[0] && lines[0].toLowerCase().replace(/"/g, '') === 'name' ? lines.slice(1) : lines;
      const names = rows
        .map(l => l.split(',')[0].replace(/^"|"$/g, '').trim())
        .filter(n => n.length >= 2);
      setImportNames(names);
    };
    reader.readAsText(file);
  };

  const handleBulkCreate = async () => {
    if (!importGrade) { setImportError('Please select a grade'); return; }
    if (importNames.length === 0) { setImportError('Upload a CSV with at least one student name'); return; }
    try {
      setImporting(true);
      setImportError(null);
      const res = await adminService.bulkCreateStudents({
        students: importNames.map(name => ({ name })),
        grade: parseInt(importGrade),
        section: importSection || undefined,
      });
      setImportResults(res.results || []);
      setImportSummary(res.summary || null);
      fetchStudents(1);
    } catch (err: any) {
      setImportError(err.message || 'Failed to import students');
    } finally {
      setImporting(false);
    }
  };

  const downloadCredentials = () => {
    if (!importResults) return;
    const esc = (v: any) => `"${(v ?? '').toString().replace(/"/g, '""')}"`;
    const created = importResults.filter(r => r.status === 'created');
    const csv = [
      ['Name', 'Login ID', 'Password', 'Grade', 'Section'].map(esc).join(','),
      ...created.map(r => [r.name, r.loginId, r.password, r.grade, r.section || ''].map(esc).join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `student_logins_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Get grade color
  const getGradeColor = (grade: number) => {
    if (grade <= 5) return 'primary';
    if (grade <= 8) return 'secondary';
    return 'success';
  };

  useEffect(() => {
    fetchStudents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && students.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >


        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Action Toolbar */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<UploadFile />}
            onClick={openImport}
          >
            Import Students
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={exportStudents}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => fetchStudents(pagination.currentPage)}
          >
            Refresh
          </Button>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '2fr 1fr 1fr 1fr 1fr'
            },
            gap: 2,
            alignItems: 'center'
          }}>
            <TextField
              fullWidth
              placeholder="Search students..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Grade</InputLabel>
              <Select
                value={filters.grade}
                onChange={(e) => handleFilterChange('grade', e.target.value)}
                label="Grade"
              >
                <MenuItem value="">All Grades</MenuItem>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(grade => (
                  <MenuItem key={grade} value={grade.toString()}>Grade {grade}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={filters.isActive}
                  onChange={(e) => handleFilterChange('isActive', e.target.checked)}
                />
              }
              label="Active Only"
            />
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                label="Sort By"
              >
                <MenuItem value="createdAt">Registration Date</MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="grade">Grade</MenuItem>
                <MenuItem value="lastLoginDate">Last Login</MenuItem>
              </Select>
            </FormControl>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              startIcon={<Search />}
            >
              Search
            </Button>
          </Box>
        </Paper>

        {/* Students Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell>Grade</TableCell>
                  <TableCell>School</TableCell>
                  <TableCell>Performance</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Registration</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((student) => (
                  <TableRow key={student._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {student.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {student.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {student.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`Grade ${student.grade}`}
                        color={getGradeColor(student.grade) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {student.school}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {student.stats.averageScore.toFixed(1)}% avg
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ({student.stats.totalTests} tests)
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={student.isActive ? 'Active' : 'Inactive'}
                        color={student.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(student.registrationDate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => openStudentDialog(student)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => {
                            openStudentDialog(student);
                            setEditMode(true);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedStudent(student);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={pagination.totalStudents || 0}
            page={pagination.currentPage - 1}
            onPageChange={handlePageChange}
            rowsPerPage={20}
            rowsPerPageOptions={[20]}
            showFirstButton
            showLastButton
          />
        </Paper>

        {/* Student Details Dialog */}
        <Dialog
          open={showStudentDialog}
          onClose={() => setShowStudentDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {editMode ? 'Edit Student' : 'Student Details'}
              </Typography>
              <IconButton onClick={() => setShowStudentDialog(false)}>
                ×
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedStudent && (
              <Box sx={{ mt: 2 }}>
                {/* Basic Information */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Basic Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                    mb: 2
                  }}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={editMode ? editFormData.name || '' : selectedStudent.name}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                      disabled={!editMode}
                    />
                    <TextField
                      fullWidth
                      label="Email"
                      value={editMode ? editFormData.email || '' : selectedStudent.email}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                      disabled={!editMode}
                    />
                    <TextField
                      fullWidth
                      label="Grade"
                      type="number"
                      value={editMode ? editFormData.grade || '' : selectedStudent.grade}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, grade: parseInt(e.target.value) }))}
                      disabled={!editMode}
                    />
                    <TextField
                      fullWidth
                      label="Phone"
                      value={editMode ? editFormData.phone || '' : selectedStudent.phone}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))}
                      disabled={!editMode}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label="School"
                    value={editMode ? editFormData.school || '' : selectedStudent.school}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, school: e.target.value }))}
                    disabled={!editMode}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2
                  }}>
                    <TextField
                      fullWidth
                      label="Parent Name"
                      value={editMode ? editFormData.parentName || '' : selectedStudent.parentName}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, parentName: e.target.value }))}
                      disabled={!editMode}
                    />
                    <TextField
                      fullWidth
                      label="Parent Phone"
                      value={editMode ? editFormData.parentPhone || '' : selectedStudent.parentPhone}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, parentPhone: e.target.value }))}
                      disabled={!editMode}
                    />
                  </Box>
                </Box>

                {/* Performance Stats */}
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Performance Statistics
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
                    gap: 2
                  }}>
                    <Card sx={{ textAlign: 'center' }}>
                      <CardContent>
                        <Typography variant="h4" color="primary.main">
                          {selectedStudent.stats.totalTests}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Tests
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ textAlign: 'center' }}>
                      <CardContent>
                        <Typography variant="h4" color="success.main">
                          {selectedStudent.stats.averageScore.toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Average Score
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ textAlign: 'center' }}>
                      <CardContent>
                        <Typography variant="h4" color="warning.main">
                          {selectedStudent.stats.bestScore}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Best Score
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ textAlign: 'center' }}>
                      <CardContent>
                        <Typography variant="h4" color="info.main">
                          {selectedStudent.stats.streak}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Current Streak
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowStudentDialog(false)}>
              Cancel
            </Button>
            {editMode ? (
              <Button variant="contained" onClick={updateStudent}>
                Save Changes
              </Button>
            ) : (
              <Button variant="contained" onClick={() => setEditMode(true)}>
                Edit Student
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete student "{selectedStudent?.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={deleteStudent}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Import Students Dialog */}
        <Dialog open={importOpen} onClose={() => !importing && setImportOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Import Students</DialogTitle>
          <DialogContent>
            {!importResults ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload a CSV with one student name per row. Accounts are created automatically with
                  unlimited access (no payment). You'll get a login ID and password for each student.
                </Typography>
                <Button size="small" startIcon={<Download />} onClick={downloadTemplate} sx={{ mb: 2 }}>
                  Download sample template
                </Button>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Grade</InputLabel>
                    <Select value={importGrade} label="Grade" onChange={(e) => setImportGrade(e.target.value)}>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((g) => (
                        <MenuItem key={g} value={g.toString()}>Grade {g}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Section (optional)"
                    value={importSection}
                    onChange={(e) => setImportSection(e.target.value)}
                  />
                </Box>
                <Button variant="outlined" component="label" startIcon={<UploadFile />} fullWidth sx={{ mb: 1 }}>
                  {importFileName || 'Choose CSV file'}
                  <input type="file" accept=".csv,text/csv" hidden onChange={handleImportFile} />
                </Button>
                {importNames.length > 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {importNames.length} student name(s) ready
                    {importNames.length <= 5 ? `: ${importNames.join(', ')}` : ''}
                  </Alert>
                )}
                {importError && <Alert severity="error" sx={{ mt: 2 }}>{importError}</Alert>}
              </Box>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Created {importSummary?.created} of {importSummary?.total} accounts
                  {importSummary && importSummary.skipped > 0 ? ` (${importSummary.skipped} skipped)` : ''}.
                  Download the credentials and share them with the students.
                </Alert>
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Login ID</TableCell>
                        <TableCell>Password</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importResults.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.name}</TableCell>
                          <TableCell>{r.loginId || '-'}</TableCell>
                          <TableCell>{r.password || `(${r.reason || 'skipped'})`}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {!importResults ? (
              <>
                <Button onClick={() => setImportOpen(false)} disabled={importing}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleBulkCreate}
                  disabled={importing}
                  startIcon={importing ? <CircularProgress size={18} /> : <UploadFile />}
                >
                  {importing ? 'Creating…' : 'Create Accounts'}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => setImportOpen(false)}>Close</Button>
                <Button variant="contained" startIcon={<Download />} onClick={downloadCredentials}>
                  Download Credentials CSV
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </motion.div>
    </Box>
  );
};

export default AdminStudents; 